import React from 'react';
import {useTranslation} from 'react-i18next';
import {isPlainObject} from '../shapes';
import {prettifyKey, humanizeValue} from '../humanize';
import {DeltaChip} from '../primitives/DeltaChip';
import {DetailFooter} from '../primitives/DetailFooter';

// Cambios hechos DIRECTO en la plataforma (los 8 PLATFORM_*). Patrón de 3 bloques:
// frase-historia → fila(s) de diff → pie con el timestamp real de la plataforma.
// Nada técnico llega a pantalla: ni el id del objeto, ni el event_type/client_type crudos,
// ni extra_data. Las Líneas de Control ya son tags en la fila — acá no se repiten.

const MAX_DIFF_ROWS = 8;
const TIME_FORMAT = {hour: '2-digit', minute: '2-digit'};
// Nombres de campaña / emails / labels ya humanizados van interpolados: sin escapeValue
// false, i18next los devuelve como entidades HTML ("Marca&#x2F;Genérica").
const RAW = {interpolation: {escapeValue: false}};

// Tipo de objeto tocado → frase CON artículo, en minúscula ("la campaña", "el anuncio").
const OBJECT_TYPE_KEYS = {
    CAMPAIGN: 'activity_log.detail.platform_object_campaign',
    ADGROUP: 'activity_log.detail.platform_object_adgroup',
    AD_GROUP: 'activity_log.detail.platform_object_adgroup',
    ADSET: 'activity_log.detail.platform_object_adgroup',
    AD_SET: 'activity_log.detail.platform_object_adgroup',
    AD: 'activity_log.detail.platform_object_ad',
    ACCOUNT: 'activity_log.detail.platform_object_account',
    AD_ACCOUNT: 'activity_log.detail.platform_object_account',
    CUSTOMER: 'activity_log.detail.platform_object_account',
};
const OBJECT_TYPE_FALLBACK = 'activity_log.detail.platform_object_generic';

// El badge de canal de la fila ya dice Google/Meta; acá se nombra el producto en la frase.
const PLATFORM_KEYS = {
    GOOGLE: 'activity_log.detail.platform_google',
    FACEBOOK: 'activity_log.detail.platform_meta',
};
const PLATFORM_FALLBACK = 'activity_log.detail.platform_generic';

function textOf(value) {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function isPrimitiveValue(value) {
    return value === null || value === undefined || typeof value !== 'object';
}

function hasValue(value) {
    return value !== null && value !== undefined && value !== '';
}

// Lado de un diff: el vacío es parte de la historia del cambio → "—", nunca "(vacío)".
function sideValue(field, value, t) {
    if (!hasValue(value)) return '—';
    const humanized = humanizeValue(field, value, t);
    return humanized === null ? '—' : humanized;
}

function toDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTime(date, language) {
    return `${date.toLocaleDateString(language)} ${date.toLocaleTimeString(language, TIME_FORMAT)}`;
}

function objectTypeText(detail, t) {
    const type = typeof detail.changed_object_type === 'string' ? detail.changed_object_type.toUpperCase() : '';
    return t(OBJECT_TYPE_KEYS[type] || OBJECT_TYPE_FALLBACK);
}

function platformText(detail, t) {
    const platform = typeof detail.source_platform === 'string' ? detail.source_platform.toUpperCase() : '';
    return t(PLATFORM_KEYS[platform] || PLATFORM_FALLBACK);
}

// Qué cambió, en palabras: field_label ya viene humanizado en ES desde el backend;
// si falta, se prettifica la key técnica; Meta puede traer solo translated_event_type.
function resolveWhat(detail, t) {
    const fieldLabel = textOf(detail.field_label);
    if (fieldLabel) return {field: fieldLabel, event: null};
    const eventLabel = textOf(detail.translated_event_type);
    if (eventLabel) return {field: null, event: eventLabel};
    const fieldChanged = textOf(detail.field_changed);
    if (fieldChanged) return {field: prettifyKey(fieldChanged), event: null};
    return {field: t('activity_log.detail.platform_field_generic'), event: null};
}

// Frase-historia. Cuatro combinaciones: actor/automático × campo/evento, cada una con su
// variante sin nombre de objeto (nunca comillas vacías en pantalla).
function buildStory(detail, t) {
    const {field, event} = resolveWhat(detail, t);
    const actor = textOf(detail.platform_actor);
    const name = textOf(detail.changed_object_name);
    const platform = platformText(detail, t);
    const object = objectTypeText(detail, t);
    // Sin actor identificado el cambio no se le puede atribuir a nadie: se narra como
    // automático de la plataforma (igual que is_system_event).
    const automatic = !!detail.is_system_event || !actor;
    const params = {actor, field, event, object, name, platform, ...RAW};

    let key;
    if (automatic && event) key = name ? 'platform_story_system_event' : 'platform_story_system_event_no_name';
    else if (automatic) key = name ? 'platform_story_system' : 'platform_story_system_no_name';
    else if (event) key = name ? 'platform_story_event' : 'platform_story_event_no_name';
    else key = name ? 'platform_story' : 'platform_story_no_name';

    return {text: t(`activity_log.detail.${key}`, params), automatic};
}

// Multi-campo (solo Google): changed_fields + old_values/new_values.
function multiFieldRows(detail) {
    const oldValues = isPlainObject(detail.old_values) ? detail.old_values : {};
    const newValues = isPlainObject(detail.new_values) ? detail.new_values : {};
    const keys = [];
    const push = key => {
        if (typeof key === 'string' && key && !keys.includes(key)) keys.push(key);
    };
    if (Array.isArray(detail.changed_fields)) detail.changed_fields.forEach(push);
    Object.keys(oldValues).forEach(push);
    Object.keys(newValues).forEach(push);

    return keys
        .map(key => ({
            key,
            label: prettifyKey(key),
            // Valores no primitivos (objetos/arrays) no se muestran crudos: quedan en "—".
            before: isPrimitiveValue(oldValues[key]) ? oldValues[key] : null,
            after: isPrimitiveValue(newValues[key]) ? newValues[key] : null,
        }))
        .filter(row => hasValue(row.before) || hasValue(row.after));
}

// Un solo campo: before/after sueltos en la raíz del detail.
function singleFieldRow(detail, t) {
    const before = isPrimitiveValue(detail.before) ? detail.before : null;
    const after = isPrimitiveValue(detail.after) ? detail.after : null;
    if (!hasValue(before) && !hasValue(after)) return [];
    const {field, event} = resolveWhat(detail, t);
    return [{key: textOf(detail.field_changed) || '', label: field || event, before, after}];
}

function buildFooter(detail, entry, t, language) {
    const parts = [];
    // El dato relevante es cuándo pasó EN la plataforma, no cuándo lo ingerimos. Si el
    // event_datetime viene corrupto, el timestamp del propio detail sigue sirviendo.
    const stamp = toDate(entry.event_datetime) || toDate(detail.platform_timestamp);
    if (stamp) {
        parts.push(t('activity_log.detail.platform_when', {
            date: stamp.toLocaleDateString(language),
            time: stamp.toLocaleTimeString(language, TIME_FORMAT),
            ...RAW,
        }));
    }
    const occurrences = Number(entry.occurrences);
    if (Number.isFinite(occurrences) && occurrences > 1) {
        const first = toDate(entry.first_seen);
        const last = toDate(entry.last_seen);
        parts.push(first && last
            ? t('activity_log.detail.platform_occurrences', {
                count: occurrences,
                first: formatDateTime(first, language),
                last: formatDateTime(last, language),
                ...RAW,
            })
            : t('activity_log.detail.platform_occurrences_simple', {count: occurrences}));
    }
    return parts.length ? parts.join(' · ') : null;
}

export function PlatformChangeDetail({detail, entry}) {
    const {t, i18n} = useTranslation();
    if (!isPlainObject(detail)) return null;

    const safeEntry = entry || {};
    const language = (i18n && i18n.language) || undefined;
    const story = buildStory(detail, t);
    const multi = multiFieldRows(detail);
    const rows = multi.length ? multi : singleFieldRow(detail, t);
    const shown = rows.slice(0, MAX_DIFF_ROWS);
    const rest = rows.length - shown.length;
    const footer = buildFooter(detail, safeEntry, t, language);

    return (
        <div className="ald-platform-change">
            <div className="ald-story">
                {story.text}
                {story.automatic && (
                    <span className="ald-delta">{t('activity_log.detail.platform_system_note')}</span>
                )}
            </div>

            {shown.length > 0 && (
                <div className="ald-diff">
                    {shown.map((row, index) => (
                        <div className="ald-diff-row" key={`${row.key}-${index}`}>
                            <span className="ald-diff-label">{row.label}</span>
                            <span className="ald-diff-values">
                                <span className="ald-diff-before">{sideValue(row.key, row.before, t)}</span>
                                <span className="ald-diff-arrow" aria-hidden="true">→</span>
                                <span className="ald-diff-after">{sideValue(row.key, row.after, t)}</span>
                                <DeltaChip before={row.before} after={row.after}/>
                            </span>
                        </div>
                    ))}
                    {rest > 0 && (
                        <div className="ald-diff-row">
                            <span className="ald-diff-label">{t('activity_log.detail.more_items', {count: rest})}</span>
                        </div>
                    )}
                </div>
            )}

            {footer && <DetailFooter>{footer}</DetailFooter>}
        </div>
    );
}

/**
 * ¿El panel agrega algo que la fila no dice ya?
 *
 * Pablo, 08/09: «Se han actualizado las reglas de valor del conjunto de anuncios…» y el detalle
 * decía lo mismo. Es real y tiene una causa exacta: cuando Meta no manda before/after (pasa en
 * `update_ad_set_value_rules`, en los renombres y en los eventos de entrega), el panel se queda
 * sólo con la frase-historia —que es una paráfrasis del título— y con el pie, que repite el
 * horario que la fila ya muestra a la derecha. Un "Ver detalle" que abre eso es una promesa de
 * que hay más, incumplida.
 *
 * Así que el toggle aparece cuando hay AL MENOS UN CAMPO con su antes y su después, que es lo
 * único que el título no cuenta. Espeja `multiFieldRows`/`singleFieldRow` — el mismo criterio de
 * descarte que el renderer, como el resto de los `hasDetail` del registry.
 */
export function platformChangeHasDetail(detail) {
    if (!isPlainObject(detail)) return false;
    if (multiFieldRows(detail).length) return true;
    return hasValue(isPrimitiveValue(detail.before) ? detail.before : null)
        || hasValue(isPrimitiveValue(detail.after) ? detail.after : null);
}

// Guard del registry: el payload tiene que oler a cambio de plataforma. Si no, el evento
// degrada a la resolución por forma (nunca se fuerza este renderer).
export function canRenderPlatformChange(detail) {
    if (!isPlainObject(detail)) return false;
    return 'source_platform' in detail
        || 'changed_object_type' in detail
        || 'field_label' in detail
        || 'field_changed' in detail
        || 'platform_timestamp' in detail
        || 'platform_event_type' in detail
        || 'translated_event_type' in detail;
}
